import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import FilterSearch from "../../components/Filtering/FilterSearch";
import SearchButton from "../../components/buttons/SearchButton";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import DataTable from "../../components/DataTable/DataTable";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useTableControls } from "../../hooks/useTableControls";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { getTextByLang } from "../../utils/formUtils";
import { formatDate } from "../../utils/constants";
import { useDeleteCatalog } from "../../hooks/catalogs/useDeleteCatalog";
import CustomButton from "../../components/buttons/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import { useGetCatalog } from "../../hooks/catalogs/useGetCatalog";
import { extractIdFromIri } from "../../utils/extractFromIri";
import OfferDetails from "../OfferingData/OfferingDetails";
import { useGetParticipantsOld } from "../../hooks/participants/useGetParticipantsOld";
import {
  buildParticipantsById,
  getContractParticipantLabel,
} from "../PrivateArea/Contracts/utils";

function DataOfferingHome() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const [loading] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    tableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    orgProvider,
    setTableData,
    setPagination,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);
  const { isProvider } = useAuthRoles();
  const { mutateAsync: deleteCatalog } = useDeleteCatalog({
    callApiWithWorker,
    t,
  });
  const participantsQ = useGetParticipantsOld({
    callApiWithWorker,
    lang: i18n.language,
    pageIndex: 1,
    pageSize: 1000,
    status: undefined,
  });

  const participantsById = useMemo(
    () => buildParticipantsById(participantsQ.data?.content),
    [participantsQ.data?.content],
  );

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const participantsLoading =
    participantsQ.isPending ||
    participantsQ.isLoading ||
    participantsQ.isFetching;

  const { data: pageResponse, isLoading } = useGetCatalog({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  useEffect(() => {
    const content = pageResponse?.content ?? [];

    const normalized = content.map((item) => ({
      ...item,
      publisherRaw: item.publisher,
      publisherId: extractIdFromIri(item.publisher),
      title: getTextByLang(item.title, i18n.language),
      description: getTextByLang(item.description, i18n.language),
      publisherLabel: participantsLoading
        ? t("common.loading")
        : getContractParticipantLabel(
            item.publisher,
            participantsById,
            i18n.language,
          ),
    }));

    setTableData(normalized);
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [
    pageResponse,
    i18n.language,
    participantsById,
    participantsLoading,
    t,
    setTableData,
    setRowCount,
  ]);

  const handleEdit = useCallback((row) => {
    const iriOrId = row?.id ?? row?.identifier;
    const id = extractIdFromIri(iriOrId);

    setSelectedOfferId(id);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedOfferId(null);
  }, []);

  const handleDelete = async (identifier) => {
    const id = extractIdFromIri(identifier);

    const r = await Swal.fire({
      icon: "warning",
      title: t("dataOfferings.deleteMessage") || "¿Borrar?",
      text:
        t("dataOfferings.deleteSubMessage") || "Esta acción es irreversible",
      showCancelButton: true,
      confirmButtonText: t("common.delete") || "Delete",
      cancelButtonText: t("common.cancel") || "Cancel",
    });

    if (!r.isConfirmed) return;

    deleteCatalog({ id });
  };

  const columns = useMemo(
    () => [
      {
        header: t("dataOfferings.offerings.dict.lastUpdate"),
        accessorKey: "updated_at",
        meta: { width: "10%" },
        cell: ({ getValue }) => {
          const date = formatDate(getValue());
          return date;
        },
      },
      {
        header: t("dataOfferings.offerings.dict.name"),
        accessorKey: "title",
        meta: { width: "35%" },
      },
      {
        header: t("dataOfferings.offerings.dict.description"),
        accessorKey: "description",
        meta: { width: "35%" },
      },
      {
        header: t("participant.userAssignment.participantLabel") || "Publisher",
        accessorKey: "publisherLabel",
        meta: { width: "20%" },
      },
    ],
    [t],
  );
  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex">
        <div key={t.language} className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("dataOfferings.title")}</h2>
          {isProvider && (
            <CustomButton
              onClick={() => navigate("/offeringdata")}
              startIcon={<AddIcon />}
            >
              {t("dataModels.createofferingbutton")}
            </CustomButton>
          )}
        </div>
        <div key={t.language} className="w86 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>
        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />
        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            <DataTable
              data={filteredData}
              columns={columns}
              onEdit={(row) => handleEdit(row)}
              onDelete={(row) => handleDelete(row.id)}
              onRowClick={(row) => handleEdit(row)}
              showActions={orgProvider}
              initialSorting={[{ id: "updated_at", desc: true }]}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
              loading={isLoading || participantsQ.isLoading}
            />
          </div>
        </div>
        <OfferDetails
          open={detailsOpen}
          onClose={handleCloseDetails}
          offerId={selectedOfferId}
          callApiWithWorker={callApiWithWorker}
          isProvider={isProvider}
        />
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
    </section>
  );
}

export default DataOfferingHome;
